/**
 * Ties the pieces together: viewport, static table layer, ball layer, playback.
 *
 * The scene is a pure VIEW. It reads keyframes and a clock; it holds no game rules and
 * decides nothing (ARCHITECTURE.md §7).
 */
import {
  DEFAULT_PHYSICS,
  type BallSnapshot,
  type SimResult,
  type TableGeometry,
} from '@potted/sim';
import { createSampler } from './animation.ts';
import { browserClock, playFrames, type Clock } from './playback.ts';
import {
  cueLengthR,
  drawAim,
  drawCue,
  readAimPalette,
  readCuePalette,
  type AimOverlay,
  type AimPalette,
  type AimStyle,
  type CuePalette,
} from './aim-layer.ts';
import { drawBalls, readBallPalette, type BallPalette, type SinkState } from './ball-layer.ts';
import { drawVectors, readVectorPalette, type VectorPalette } from './vector-layer.ts';
import { frameWidthM, pocketHole } from './frame.ts';
import { renderTableLayer } from './table-layer.ts';
import { renderResolution } from './resolution.ts';
import { createViewport, type Viewport, type ViewportOptions } from './viewport.ts';

export interface Scene {
  /** The current mapping, so callers can turn a touch point into table coordinates. */
  viewport(): Viewport;
  /** Re-measure and redraw: window resize, browser zoom, pinch zoom, a new screen. */
  resize(): void;
  /** Orientation and size preferences from Settings (UI-004). Redraws. */
  setDisplay(options: ViewportOptions): void;
  /** Draw the balls where they currently are, with nothing in motion. */
  showAtRest(balls: readonly BallSnapshot[]): void;
  /**
   * Show (or clear) the aim guide. Pure geometry, supplied by the caller — the scene
   * never derives it, and never sees a `SimResult` before the animation clock does.
   */
  setAim(aim: AimOverlay | undefined): void;
  /** How the guide shows power, and whether the cue is drawn (UI-015). Redraws. */
  setAimStyle(style: AimStyle): void;
  /**
   * Animate a simulated shot. Resolves when playback reaches the end. `onFrame`, if
   * given, is told each frame's playback time, AFTER the frame is drawn, so anything
   * keyed to the clock (sound, SND-001) can never run ahead of what is on screen.
   */
  play(result: SimResult, onFrame?: (tS: number) => void): Promise<void>;
  /**
   * Stop playback and show one exact instant of a shot, for scrubbing in the debug
   * panel (DBG-001). The caller supplies the state — the scene does not simulate.
   */
  scrub(balls: readonly BallSnapshot[]): void;
  /** Draw velocity, contact-patch slip and sidespin on every ball (DBG-001). */
  setVectors(on: boolean): void;
  /** Playback rate. 1 is real time; 0.25 is quarter speed. */
  setTimeScale(scale: number): void;
  destroy(): void;
}

/* Breathing room outside the rails. The rails themselves are measured in metres by
   `frame.ts`, so this no longer has to be big enough to hide them (L36). */
const TABLE_MARGIN_PX = 8;

/** How long a potted ball takes to sink out of sight (UI-004). */
const POT_SINK_S = 0.3;

/** Honour the OS "reduce motion" setting: a potted ball then just goes. */
function sinkDurationS(): number {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : POT_SINK_S;
}

export function createScene(
  canvas: HTMLCanvasElement,
  geometry: TableGeometry,
  clock: Clock = browserClock,
): Scene {
  const maybeCtx = canvas.getContext('2d');
  if (maybeCtx === null) throw new Error('createScene: could not get a 2D context');
  const ctx: CanvasRenderingContext2D = maybeCtx;

  const frameM = frameWidthM(geometry);
  let display: ViewportOptions = {};
  let viewport: Viewport = buildViewport();
  let table = renderTableLayer(geometry, viewport);
  let palette: BallPalette = readBallPalette();
  let aimPalette: AimPalette = readAimPalette();
  let cuePalette: CuePalette = readCuePalette();
  let vectorPalette: VectorPalette = readVectorPalette();
  let aim: AimOverlay | undefined;
  let aimStyle: AimStyle = { power: 0.5, cue: true, sideR: 0, lengthR: cueLengthR(0.5) };

  /*
   * UI-017: the cue's own layer, over the whole screen. The table canvas is exactly the
   * table's box, so a cue drawn on it was chopped wherever the white ball sat near a
   * cushion and the shot pointed away from it. On this layer the cue reaches past the
   * table, as a real cue reaches over the rail, and fades out. It takes no pointer
   * events: every tap goes through to what is underneath.
   */
  const cueLayer = document.createElement('canvas');
  cueLayer.className = 'cue-layer';
  cueLayer.setAttribute('aria-hidden', 'true');
  document.body.append(cueLayer);
  const cueCtx = cueLayer.getContext('2d');

  function sizeCueLayer(): void {
    cueLayer.width = Math.ceil(window.innerWidth * viewport.dpr);
    cueLayer.height = Math.ceil(window.innerHeight * viewport.dpr);
  }

  sizeCueLayer();

  /** Clear the cue layer, and draw the cue where the table canvas is now. */
  function paintCue(): void {
    if (cueCtx === null) return;
    cueCtx.setTransform(1, 0, 0, 1, 0, 0);
    cueCtx.clearRect(0, 0, cueLayer.width, cueLayer.height);
    if (aim === undefined || !aimStyle.cue) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = viewport.dpr;
    // Read each time it is drawn: a new cue style (UI-019) or theme shows at once.
    cuePalette = readCuePalette();
    // Table-canvas pixels, placed where that canvas sits on the screen, and stretched as
    // the canvas itself is if its box has changed since it was last measured (UI-021),
    // so the cue always lines up with the table as drawn.
    const sx = viewport.cssWidth > 0 ? rect.width / viewport.cssWidth : 1;
    const sy = viewport.cssHeight > 0 ? rect.height / viewport.cssHeight : 1;
    cueCtx.setTransform(dpr * sx, 0, 0, dpr * sy, rect.left * dpr, rect.top * dpr);
    drawCue(
      cueCtx,
      viewport,
      cuePalette,
      DEFAULT_PHYSICS.ballRadiusM,
      aim,
      aimStyle.power,
      aimStyle.sideR,
      aimStyle.lengthR,
    );
  }
  let vectors = false;

  let ballCount = 0;
  let xM = new Float64Array(0);
  let yM = new Float64Array(0);
  let potted = new Uint8Array(0);
  /* Motion, for the debug overlay only. Kept as scratch arrays alongside the
     positions so a frame with vectors on still allocates nothing. Zeroed whenever
     positions come from keyframes, which carry no velocity or spin. */
  let vxMps = new Float64Array(0);
  let vyMps = new Float64Array(0);
  let wxRadS = new Float64Array(0);
  let wyRadS = new Float64Array(0);
  let wzRadS = new Float64Array(0);
  // Seconds since each ball dropped, for the sinking animation. Scratch like the rest.
  let sinceDropS = new Float64Array(0);
  // A potted ball sinks into the hole as DRAWN, which sits behind the mouth (TBL-002).
  const pocketsXM = Float64Array.from(geometry.pockets, (p) => pocketHole(p).xM);
  const pocketsYM = Float64Array.from(geometry.pockets, (p) => pocketHole(p).yM);
  let sink: SinkState | undefined;
  let current: { stop(): void } | undefined;
  let timeScale = 1;

  function buildViewport(): Viewport {
    const rect = canvas.getBoundingClientRect();
    return createViewport(
      geometry.spec,
      rect.width,
      rect.height,
      // Includes pinch zoom, which devicePixelRatio does not (resolution.ts).
      renderResolution(
        rect.width,
        rect.height,
        window.devicePixelRatio,
        window.visualViewport?.scale ?? 1,
      ),
      TABLE_MARGIN_PX,
      frameM,
      display,
    );
  }

  function ensureCapacity(count: number): void {
    if (count <= xM.length) return;
    xM = new Float64Array(count);
    yM = new Float64Array(count);
    potted = new Uint8Array(count);
    vxMps = new Float64Array(count);
    vyMps = new Float64Array(count);
    wxRadS = new Float64Array(count);
    wyRadS = new Float64Array(count);
    wzRadS = new Float64Array(count);
    sinceDropS = new Float64Array(count);
  }

  /** Keyframes carry positions only, so motion is unknown while one is being played. */
  function clearMotion(): void {
    vxMps.fill(0);
    vyMps.fill(0);
    wxRadS.fill(0);
    wyRadS.fill(0);
    wzRadS.fill(0);
  }

  const paint = (): void => {
    // A page loaded with no layout box (a background tab, a collapsed pane) has a 0x0
    // table layer, and drawImage throws on one. Thrown from the first paint, that
    // aborted main.ts before its resize listener existed, so the table stayed blank
    // for good (CLAUDE.md §13/L34). Nothing to draw into yet; the next resize paints.
    if (table.canvas.width === 0 || table.canvas.height === 0) return;
    ctx.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.cssWidth, viewport.cssHeight);
    ctx.drawImage(table.canvas, 0, 0, viewport.cssWidth, viewport.cssHeight);
    // Under the balls, so the cue ball sits on top of its own guide line.
    if (aim !== undefined) {
      drawAim(ctx, viewport, aimPalette, DEFAULT_PHYSICS.ballRadiusM, aim, aimStyle);
    }
    drawBalls(ctx, viewport, palette, DEFAULT_PHYSICS.ballRadiusM, xM, yM, potted, ballCount, sink);
    // Over the balls, and over the page: a cue lies above the table (UI-017).
    paintCue();
    if (vectors) {
      drawVectors(ctx, viewport, vectorPalette, DEFAULT_PHYSICS.ballRadiusM, {
        xM,
        yM,
        vxMps,
        vyMps,
        wxRadS,
        wyRadS,
        wzRadS,
        potted,
        count: ballCount,
      });
    }
  };

  function adoptSnapshots(balls: readonly BallSnapshot[]): void {
    ballCount = balls.length;
    ensureCapacity(ballCount);
    // At rest nothing is mid-sink; a potted ball is simply gone.
    sink = undefined;
    for (let i = 0; i < ballCount; i++) {
      const ball = balls[i];
      if (ball === undefined) continue;
      xM[i] = ball.xM;
      yM[i] = ball.yM;
      potted[i] = ball.potted ? 1 : 0;
      vxMps[i] = ball.vxMps;
      vyMps[i] = ball.vyMps;
      wxRadS[i] = ball.wxRadS;
      wyRadS[i] = ball.wyRadS;
      wzRadS[i] = ball.wzRadS;
    }
  }

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    viewport = buildViewport();
    canvas.width = Math.ceil(rect.width * viewport.dpr);
    canvas.height = Math.ceil(rect.height * viewport.dpr);
    sizeCueLayer();
    // Tokens may have changed with the theme, so re-read them with the re-render.
    palette = readBallPalette();
    aimPalette = readAimPalette();
    cuePalette = readCuePalette();
    vectorPalette = readVectorPalette();
    table = renderTableLayer(geometry, viewport);
    paint();
  }

  return {
    viewport: () => viewport,

    resize,

    setDisplay(options) {
      display = options;
      resize();
    },

    showAtRest(balls) {
      adoptSnapshots(balls);
      paint();
    },

    setAim(next) {
      aim = next;
      paint();
    },

    setAimStyle(next) {
      if (
        next.power === aimStyle.power &&
        next.cue === aimStyle.cue &&
        next.sideR === aimStyle.sideR &&
        next.lengthR === aimStyle.lengthR
      ) {
        return;
      }
      aimStyle = next;
      paint();
    },

    play(result, onFrame) {
      current?.stop();
      aim = undefined;
      // Which balls are down comes from the sampler frame by frame, never from
      // `finalState` up front — that is the end of the shot, not the start (L35).
      const sampler = createSampler(result);
      ballCount = result.keyframes.ballCount;
      ensureCapacity(ballCount);
      const sinkS = sinkDurationS();
      sink = { sinceDropS, durationS: sinkS, pocketsXM, pocketsYM };

      const { playback, done } = playFrames(
        // A ball potted at the very end still gets to sink before the shot settles.
        sampler.endS(sinkS),
        clock,
        (tS) => {
          sampler.sample(tS, xM, yM, potted, sinceDropS);
          clearMotion();
          paint();
          onFrame?.(tS);
        },
        () => {
          adoptSnapshots(result.finalState);
          paint();
        },
        timeScale,
      );
      current = playback;
      return done;
    },

    scrub(balls) {
      current?.stop();
      // The guide belongs to the shot being AIMED, not to a frame being inspected.
      // Left on, it draws the next shot's line over the last shot's mid-flight balls.
      aim = undefined;
      adoptSnapshots(balls);
      paint();
    },

    setVectors(on) {
      vectors = on;
      paint();
    },

    setTimeScale(scale) {
      timeScale = scale;
    },

    destroy() {
      current?.stop();
      cueLayer.remove();
    },
  };
}
